import { useState } from "react";
import { Link } from "react-router-dom";
import { buildersApi, ApiError } from "@/api";
import type { JobBuilder } from "@/types";
import { Button, Pill } from "@/components/ui";
import { chatVisibilityLabel, jobBuilderRoleLabel } from "../builders/BuilderDetail";
import { InvitePersonDrawer } from "../builders/InvitePersonDrawer";
import { AddBuilderDrawer } from "./AddBuilderDrawer";
import { JobBuilderLinkDrawer } from "./JobBuilderLinkDrawer";
import "../builders/builders.css";

type Drawer = "add" | { kind: "link"; b: JobBuilder } | { kind: "invite"; b: JobBuilder } | null;

/** Builders on this job — a section beneath the clients on the Clients tab
 *  (Builders Build Brief 01, D10). Renders detail.builders[] (live links only,
 *  server-sorted by when they were added); every action re-fetches /detail. */
export function JobBuildersSection({
  jobId,
  builders,
  onChanged,
  onError,
  onNotice,
}: {
  jobId: number;
  builders: JobBuilder[];
  onChanged: () => void;
  onError: (message: string) => void;
  onNotice: (kind: "info" | "warn", message: string) => void;
}) {
  const [drawer, setDrawer] = useState<Drawer>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  async function remove(b: JobBuilder) {
    if (busyId != null) return;
    if (!window.confirm(`Remove ${b.company_name} from this job? Their app logins lose the chat immediately, and the client will see a note that they were removed.`)) return;
    setBusyId(b.builder_id);
    try {
      await buildersApi.detachFromJob(jobId, b.builder_id);
      onNotice("info", `${b.company_name} was removed from the job.`);
      onChanged();
    } catch (err) {
      onError(err instanceof ApiError ? err.message : "Could not remove the builder.");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="job-builders">
      <div className="job-builders__head">
        <div>
          <h3>Builders</h3>
          <p className="job-drawer__muted">
            {builders.length === 0
              ? "No builders on this job."
              : `${builders.length} builder${builders.length === 1 ? "" : "s"} on this job — their app logins share the chat.`}
          </p>
        </div>
        <Button variant="secondary" size="sm" onClick={() => setDrawer("add")}>Add builder</Button>
      </div>

      {builders.length > 0 ? (
        <ul className="job-client-cards">
          {builders.map((b) => {
            const busy = busyId === b.builder_id;
            const people = b.people ?? [];
            const activePeople = people.filter((p) => p.is_active);
            return (
              <li key={b.job_builder_id} className="job-client-card">
                <div className="job-client-card__main">
                  <div className="job-client-card__title-row">
                    <span className="job-client-card__name">
                      <span className="job-client-tag" aria-label="Builder">🔨</span>
                      <Link to={`/staff/builders/${b.builder_id}`}>{b.company_name}</Link>
                    </span>
                    <Pill tone="info">{jobBuilderRoleLabel(b.role)}</Pill>
                  </div>
                  {b.contact_name || b.email || b.phone ? (
                    <p className="job-client-card__contact">
                      {[b.contact_name, b.email, b.phone].filter(Boolean).join(" · ")}
                    </p>
                  ) : null}
                  <p className="job-builder-card__line">{chatVisibilityLabel(b.chat_from)}</p>
                  <p className="job-builder-card__line">
                    {activePeople.length === 0
                      ? "No app logins yet — invite a person to bring them into the chat."
                      : `In the chat: ${activePeople.map((p) => `${p.name || p.email}${p.has_signed_in ? "" : " (invited)"}`).join(", ")}`}
                  </p>
                </div>
                <div className="job-client-card__actions">
                  <Button variant="ghost" size="sm" disabled={busy} onClick={() => setDrawer({ kind: "link", b })}>Edit</Button>
                  <Button variant="secondary" size="sm" disabled={busy} onClick={() => setDrawer({ kind: "invite", b })}>Invite person</Button>
                  <Button variant="ghost" size="sm" loading={busy} onClick={() => remove(b)}>Remove</Button>
                </div>
              </li>
            );
          })}
        </ul>
      ) : null}

      {drawer === "add" ? (
        <AddBuilderDrawer
          jobId={jobId}
          attachedIds={builders.map((b) => b.builder_id)}
          onClose={() => setDrawer(null)}
          onSaved={(res) => {
            setDrawer(null);
            onNotice("info", res.already_attached ? `${res.company_name} was already on this job.` : `${res.company_name} added to the job.`);
            onChanged();
          }}
        />
      ) : null}

      {drawer && drawer !== "add" && drawer.kind === "link" ? (
        <JobBuilderLinkDrawer
          jobId={jobId}
          link={drawer.b}
          onClose={() => setDrawer(null)}
          onSaved={() => {
            setDrawer(null);
            onNotice("info", "Builder link updated.");
            onChanged();
          }}
        />
      ) : null}

      {drawer && drawer !== "add" && drawer.kind === "invite" ? (
        <InvitePersonDrawer
          builderId={drawer.b.builder_id}
          builderName={drawer.b.company_name}
          defaultName={(drawer.b.people ?? []).length === 0 ? drawer.b.contact_name : ""}
          defaultEmail={(drawer.b.people ?? []).length === 0 ? drawer.b.email : ""}
          onClose={() => setDrawer(null)}
          onInvited={(res) => {
            setDrawer(null);
            if (res.email_sent) onNotice("info", res.created ? `Invited — app links sent to ${res.email}.` : `Invite resent to ${res.email}.`);
            else if (res.created) onNotice("info", `Login created for ${res.email}${res.email_reason === "not_requested" ? " (no email sent)." : ", but no email was sent — the invite template isn't set up in SendGrid yet."}`);
            else onNotice("info", `${res.email} already has access.`);
            onChanged();
          }}
        />
      ) : null}
    </div>
  );
}
